import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import type { PageCover } from '@noti/shared';
import type { JSONContent } from '@tiptap/core';

import {
  createCommentThread,
  createPage,
  deleteComment,
  deletePage,
  editComment,
  fetchPage,
  fetchPageViewers,
  fetchPages,
  fetchTrash,
  fetchUnreadCount,
  fetchWorkspaces,
  inviteToWorkspace,
  listComments,
  listWorkspaceMembers,
  logout,
  recordPageView,
  replyToCommentThread,
  restorePage,
  saveDocument,
  searchPages,
  setCommentThreadResolved,
  setFavorite,
  trashPage,
  updatePage,
  uploadFile,
} from '../api';
import type {
  CommentThread,
  CurrentUser,
  PageDetail,
  PageSummary,
  PageViewer,
  WorkspaceMember,
  WorkspaceSummary,
} from '../api';
import { Logo } from '../components/Logo';
import { t } from '../i18n';
import { useTheme } from '../theme';
import { ActionPopover } from './ActionPopover';
import { EmojiPicker } from './EmojiPicker';
import { mediaUrl } from './Media';
import { PageIcon } from './PageIcon';
import { Glyph, ICON } from './icons';
import { PageEditor } from './PageEditor';
import { DatabaseRowProperties } from './DatabaseRowProperties';
import { CookieNotice } from './CookieNotice';
import { LegalPage } from './LegalPage';
import { PageActionMenu } from './PageActionMenu';
import { AccountMenu, DocumentMenu, WorkspaceMenu } from './ShellActionMenus';
import { SettingsDialog } from './SettingsDialog';
import { InboxPanel } from './InboxPanel';
import { ShareDialog } from './ShareDialog';
import { MobileDrawer } from './MobileDrawer';
import { MobileMenu } from './MobileMenu';
import { useIsMobile, useVisualViewport } from './useIsMobile';
import type { PageEditorActions, PageEditorComments } from './PageEditor';
import {
  ancestorIds,
  closeTab,
  recordVisit,
  stepBack,
  stepForward,
  type WorkspaceTab,
} from './tabs';
import { ToastProvider, useToast } from './Toast';

const COVERS = [
  { id: 'tone:selection', label: 'page.cover.selection' as const, className: 'cover--selection' },
  { id: 'tone:info', label: 'page.cover.info' as const, className: 'cover--info' },
  { id: 'tone:warning', label: 'page.cover.warning' as const, className: 'cover--warning' },
  { id: 'tone:success', label: 'page.cover.success' as const, className: 'cover--success' },
] as const;

type Route =
  { kind: 'page'; id: string } | { kind: 'home' } | { kind: 'legal'; page: 'privacy' | 'terms' };

function readRoute(): Route {
  const hash = window.location.hash.replace(/^#/, '');
  const match = /^p\/([0-9a-f-]{36})$/i.exec(hash);
  if (match?.[1]) return { kind: 'page', id: match[1] };
  const legal = /^legal\/(privacy|terms)$/.exec(hash);
  if (legal?.[1]) return { kind: 'legal', page: legal[1] as 'privacy' | 'terms' };
  return { kind: 'home' };
}

function byPosition(a: PageSummary, b: PageSummary): number {
  return a.position < b.position ? -1 : a.position > b.position ? 1 : 0;
}

/** Prototype shows an emoji icon, never a data-URL image, in tabs and crumbs. */
function pageIcon(icon: string | null): string | null {
  if (!icon || icon.startsWith('data:')) return null;
  return icon;
}

type Crumb = {
  key: string;
  label: string;
  icon: string | null;
  current: boolean;
  onOpen: () => void;
};

function crumbsFor(
  page: PageDetail,
  workspaces: WorkspaceSummary[],
  pages: PageSummary[],
  showHome: () => void,
  showPage: (id: string) => void,
): Crumb[] {
  const crumbs: Crumb[] = [];
  const workspace = workspaces.find((item) => item.id === page.workspaceId);
  if (workspace) {
    crumbs.push({
      key: `ws:${workspace.id}`,
      label: workspace.name,
      icon: null,
      current: false,
      onOpen: showHome,
    });
  }
  for (const id of ancestorIds(pages, page.id)) {
    const ancestor = pages.find((item) => item.id === id);
    if (!ancestor) continue;
    crumbs.push({
      key: ancestor.id,
      label: ancestor.title || t('page.untitled'),
      icon: pageIcon(ancestor.icon),
      current: false,
      onOpen: () => showPage(ancestor.id),
    });
  }
  crumbs.push({
    key: page.id,
    label: page.title || t('page.untitled'),
    icon: pageIcon(page.icon),
    current: true,
    onOpen: () => undefined,
  });
  return crumbs;
}

function tabForRoute(): WorkspaceTab {
  const initial = readRoute();
  return {
    id: 'tab-1',
    pageId: initial.kind === 'page' ? initial.id : null,
    back: [],
    fwd: [],
  };
}

/** "just now", "5 minutes ago", "2 hours ago" — for a real page view. */
function timeAgo(iso: string): string {
  const minutes = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60_000));
  if (minutes < 1) return t('presence.justNow');
  if (minutes === 1) return t('presence.minuteAgo');
  if (minutes < 60) return t('presence.minutesAgo', { count: String(minutes) });
  const hours = Math.round(minutes / 60);
  if (hours === 1) return t('presence.hourAgo');
  if (hours < 24) return t('presence.hoursAgo', { count: String(hours) });
  const days = Math.round(hours / 24);
  return days === 1 ? t('presence.dayAgo') : t('presence.daysAgo', { count: String(days) });
}

/** "Arko Goossens" → "AG"; single word falls back to its first two letters. */
function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const first = words[0] ?? '?';
  if (words.length <= 1) return first.slice(0, 2).toUpperCase();
  const last = words[words.length - 1] ?? first;
  return ((first[0] ?? '') + (last[0] ?? '')).toUpperCase();
}

export function WorkspaceShell({
  user,
  onSignedOut,
}: {
  user: CurrentUser;
  onSignedOut: () => void;
}): React.ReactElement {
  return (
    <ToastProvider>
      <Shell user={user} onSignedOut={onSignedOut} />
    </ToastProvider>
  );
}

function Shell({
  user,
  onSignedOut,
}: {
  user: CurrentUser;
  onSignedOut: () => void;
}): React.ReactElement {
  const [workspaces, setWorkspaces] = useState<WorkspaceSummary[] | null>(null);
  const [pagesByWs, setPagesByWs] = useState<Record<string, PageSummary[]>>({});
  const [page, setPage] = useState<PageDetail | null>(null);
  const [viewers, setViewers] = useState<PageViewer[]>([]);
  const [route, setRoute] = useState<Route>(readRoute);
  const [tabs, setTabs] = useState<WorkspaceTab[]>(() => [tabForRoute()]);
  const [activeTabId, setActiveTabId] = useState('tab-1');
  const activeTabIdRef = useRef(activeTabId);
  const skipHistoryRef = useRef(false);
  const tabSeq = useRef(1);
  const [error, setError] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [inboxOpen, setInboxOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [searchOpen, setSearchOpen] = useState(false);
  const [trashOpen, setTrashOpen] = useState(false);
  const [sidebarClosed, setSidebarClosed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [crumbsOpen, setCrumbsOpen] = useState(false);
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const isMobile = useIsMobile();
  useVisualViewport();
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  // A delayed close event of one menu must not close a menu that opened since.
  const onMenuOpenChange =
    (menuId: string) =>
    (next: boolean): void =>
      setOpenMenu((current) => (next ? menuId : current === menuId ? null : current));
  const [dragging, setDragging] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<
    { id: string; pos: 'before' | 'after' | 'in' } | { id: string; pos: 'ws' } | null
  >(null);
  const [focusTitleNonce, setFocusTitleNonce] = useState(0);
  const [threads, setThreads] = useState<CommentThread[]>([]);
  const [threadsLoaded, setThreadsLoaded] = useState(false);
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [showResolved, setShowResolved] = useState(false);
  const [authorFilter, setAuthorFilter] = useState('all');
  const [commentsHidden, setCommentsHidden] = useState(false);
  const [pageComposeOpen, setPageComposeOpen] = useState(false);
  const [markdown, setMarkdown] = useState<{ mode: 'import' | 'export'; text: string } | null>(
    null,
  );
  const pageActions = useRef<PageEditorActions | null>(null);

  const toast = useToast();
  const { preference, setPreference } = useTheme();
  const allPages = useMemo(() => Object.values(pagesByWs).flat().sort(byPosition), [pagesByWs]);

  const reloadPages = useCallback(async (listed: WorkspaceSummary[]) => {
    const entries = await Promise.all(
      listed.map(async (ws) => [ws.id, await fetchPages(ws.id)] as const),
    );
    setPagesByWs(Object.fromEntries(entries));
  }, []);

  useEffect(() => {
    let cancelled = false;
    void fetchWorkspaces()
      .then((listed) => {
        if (cancelled) return;
        setWorkspaces(listed);
        return reloadPages(listed);
      })
      .catch(() => {
        if (!cancelled) setError(t('error.generic'));
      });
    return () => {
      cancelled = true;
    };
  }, [reloadPages]);

  // Presence: record this visit, then show who else opened the page. A failed
  // presence call never blocks the page; it is a signal, not content.
  const openPageId = page?.id;
  useEffect(() => {
    if (!openPageId) {
      setViewers([]);
      return;
    }
    let cancelled = false;
    void recordPageView(openPageId)
      .then(() => fetchPageViewers(openPageId))
      .then((list) => {
        if (!cancelled) setViewers(list);
      })
      .catch(() => undefined);
    const timer = window.setInterval(() => {
      void fetchPageViewers(openPageId)
        .then((list) => {
          if (!cancelled) setViewers(list);
        })
        .catch(() => undefined);
    }, 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [openPageId]);

  // The badge next to Inbox is the only thing that needs the count on its own.
  useEffect(() => {
    let cancelled = false;
    void fetchUnreadCount()
      .then((count) => {
        if (!cancelled) setUnread(count);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onHash = (): void => setRoute(readRoute());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // Navigation always closes the mobile navigation; the page itself becomes visible.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [route]);

  // Hash and setRoute are the only navigation signals. Back, forward and tab
  // switches set skipHistoryRef so this does not record that visit twice.
  useEffect(() => {
    const pageId = route.kind === 'page' ? route.id : null;
    const tabId = activeTabIdRef.current;
    const skip = skipHistoryRef.current;
    skipHistoryRef.current = false;
    setTabs((current) => {
      const next = current.map((tab) => {
        if (tab.id !== tabId) return tab;
        if (skip) return tab.pageId === pageId ? tab : { ...tab, pageId };
        return recordVisit(tab, pageId);
      });
      return next.every((tab, index) => tab === current[index]) ? current : next;
    });
  }, [route]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setSearchOpen(true);
      }
      if ((event.metaKey || event.ctrlKey) && event.key === '\\') {
        event.preventDefault();
        setSidebarClosed((closed) => !closed);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (route.kind !== 'page') {
      setPage(null);
      return;
    }
    let cancelled = false;
    void fetchPage(route.id)
      .then((detail) => {
        if (!cancelled) setPage(detail);
      })
      .catch(() => {
        if (!cancelled) setError(t('error.generic'));
      });
    return () => {
      cancelled = true;
    };
  }, [route]);

  useEffect(() => {
    if (!page) {
      setThreads([]);
      setThreadsLoaded(false);
      return;
    }
    let cancelled = false;
    setThreadsLoaded(false);
    void listComments(page.id)
      .then((listed) => {
        if (!cancelled) {
          setThreads(listed);
          setThreadsLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) setThreadsLoaded(false);
      });
    void listWorkspaceMembers(page.workspaceId)
      .then((listed) => {
        if (!cancelled) setMembers(listed);
      })
      .catch(() => {
        if (!cancelled) setMembers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [page?.id, page?.workspaceId]);

  const refreshThreads = useCallback(async (): Promise<void> => {
    if (!page) return;
    setThreads(await listComments(page.id));
  }, [page]);

  const editorComments: PageEditorComments = {
    loaded: threadsLoaded,
    threads,
    members,
    currentUserId: user.id,
    showResolved,
    authorFilter,
    hidden: commentsHidden,
    pageComposeOpen,
    onClosePageCompose: () => setPageComposeOpen(false),
    onShowComments: () => setCommentsHidden(false),
    onCreateThread: async (quote, text) => {
      if (!page) throw new Error('no page');
      const thread = await createCommentThread(page.id, { quote, text });
      setThreads((current) => [...current, thread]);
      return thread;
    },
    onReply: async (threadId, text) => {
      if (!page) return;
      await replyToCommentThread(page.id, threadId, text);
      await refreshThreads();
    },
    onEdit: async (commentId, text) => {
      if (!page) return;
      await editComment(page.id, commentId, text);
      await refreshThreads();
    },
    onDelete: async (commentId) => {
      if (!page) return;
      await deleteComment(page.id, commentId);
      await refreshThreads();
    },
    onSetResolved: async (threadId, resolved) => {
      if (!page) return;
      await setCommentThreadResolved(page.id, threadId, resolved);
      await refreshThreads();
    },
  };

  const openThreadCount = threads.filter((thread) => !thread.resolved).length;
  const resolvedThreadCount = threads.length - openThreadCount;
  const threadAuthors = [
    ...new Set(threads.flatMap((thread) => thread.messages.map((message) => message.authorName))),
  ].sort();

  useEffect(() => {
    const onDown = (event: MouseEvent): void => {
      if (!(event.target as HTMLElement).closest('[data-menu-root]')) setOpenMenu(null);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, []);

  const showPage = useCallback((id: string): void => {
    window.location.hash = `p/${id}`;
    setRoute({ kind: 'page', id });
  }, []);

  const showHome = useCallback((): void => {
    if (window.location.hash) window.location.hash = '';
    setRoute({ kind: 'home' });
  }, []);

  const openPage = showPage;

  const reveal = useCallback(
    (pageId: string | null): void => {
      const shown = route.kind === 'page' ? route.id : null;
      if (shown === pageId) return;
      skipHistoryRef.current = true;
      if (pageId) showPage(pageId);
      else showHome();
    },
    [route, showPage, showHome],
  );

  const openInNewTab = useCallback(
    (pageId: string | null): void => {
      if (isMobile) {
        setMobileNavOpen(false);
        reveal(pageId);
        return;
      }
      tabSeq.current += 1;
      const id = `tab-${tabSeq.current}`;
      activeTabIdRef.current = id;
      setActiveTabId(id);
      setTabs((current) => [...current, { id, pageId, back: [], fwd: [] }]);
      reveal(pageId);
    },
    [isMobile, reveal],
  );

  const switchTo = useCallback(
    (id: string): void => {
      const target = tabs.find((tab) => tab.id === id);
      if (!target || target.id === activeTabId) return;
      activeTabIdRef.current = id;
      setActiveTabId(id);
      reveal(target.pageId);
    },
    [tabs, activeTabId, reveal],
  );

  const onCloseTab = useCallback(
    (id: string): void => {
      const result = closeTab(tabs, activeTabId, id);
      if (!result) return;
      setTabs(result.tabs);
      if (result.activeId === activeTabId) return;
      const next = result.tabs.find((tab) => tab.id === result.activeId);
      activeTabIdRef.current = result.activeId;
      setActiveTabId(result.activeId);
      reveal(next ? next.pageId : null);
    },
    [tabs, activeTabId, reveal],
  );

  const onBack = useCallback((): void => {
    const current = tabs.find((tab) => tab.id === activeTabId);
    if (!current) return;
    const next = stepBack(current);
    if (!next) return;
    setTabs((list) => list.map((tab) => (tab.id === current.id ? next : tab)));
    reveal(next.pageId);
  }, [tabs, activeTabId, reveal]);

  const onForward = useCallback((): void => {
    const current = tabs.find((tab) => tab.id === activeTabId);
    if (!current) return;
    const next = stepForward(current);
    if (!next) return;
    setTabs((list) => list.map((tab) => (tab.id === current.id ? next : tab)));
    reveal(next.pageId);
  }, [tabs, activeTabId, reveal]);

  const onTabListKey = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    event.preventDefault();
    const index = tabs.findIndex((tab) => tab.id === activeTabId);
    const delta = event.key === 'ArrowRight' ? 1 : -1;
    const next = tabs[(index + delta + tabs.length) % tabs.length];
    if (next) switchTo(next.id);
  };

  const refresh = useCallback(() => {
    if (!workspaces) return;
    void reloadPages(workspaces).catch(() => setError(t('error.generic')));
  }, [workspaces, reloadPages]);

  const onCreate = async (workspaceId: string, parentId: string | null): Promise<void> => {
    setError(null);
    try {
      const created = await createPage(workspaceId, { parentId });
      await reloadPages(workspaces ?? []);
      openPage(created.id);
      setFocusTitleNonce((n) => n + 1);
    } catch {
      setError(t('error.generic'));
    }
  };

  const movePage = async (
    pageId: string,
    parentId: string | null,
    beforeId: string | null,
  ): Promise<void> => {
    try {
      await updatePage(pageId, { parentId, beforeId });
      await reloadPages(workspaces ?? []);
    } catch {
      setError(t('error.generic'));
    }
  };

  const onDuplicate = async (target: PageSummary): Promise<void> => {
    try {
      const source = await fetchPage(target.id);
      const copy = await createPage(source.workspaceId, {
        title: `${source.title || t('page.untitled')} (copy)`,
        parentId: source.parentId,
      });
      await saveDocument(copy.id, 1, source.document.content as JSONContent);
      await reloadPages(workspaces ?? []);
      openPage(copy.id);
      toast(t('toast.duplicated'));
    } catch {
      setError(t('error.generic'));
    }
  };

  const onToggleFavorite = async (target: PageSummary): Promise<void> => {
    try {
      await setFavorite(target.id, !target.favorite);
      if (page?.id === target.id) setPage({ ...page, favorite: !target.favorite });
      await reloadPages(workspaces ?? []);
    } catch {
      setError(t('error.generic'));
    }
  };

  const copyLink = (id: string, message: string): void => {
    const url = `${window.location.origin}${window.location.pathname}#p/${id}`;
    void navigator.clipboard
      ?.writeText(url)
      .then(() => toast(message))
      .catch(() => toast(t('error.generic')));
  };

  const rename = (id: string): void => {
    openPage(id);
    setFocusTitleNonce((n) => n + 1);
  };

  if (workspaces === null) {
    return (
      <main className="shell shell--loading">
        <p role="status">{t('workspace.loading')}</p>
      </main>
    );
  }

  const activeTab = tabs.find((tab) => tab.id === activeTabId) ?? tabs[0];
  const currentSummary = page ? (allPages.find((item) => item.id === page.id) ?? null) : null;
  const crumbs = page ? crumbsFor(page, workspaces, allPages, showHome, showPage) : [];
  const currentCrumb = crumbs[crumbs.length - 1] ?? null;
  const otherViewers = viewers.filter((viewer) => viewer.userId !== user.id).slice(0, 3);

  const onTrashPage = async (target: PageSummary): Promise<void> => {
    await trashPage(target.id);
    if (page?.id === target.id) showHome();
    refresh();
  };

  const primary = workspaces[0];
  if (!primary) {
    return (
      <main className="shell shell--loading">
        <p>{t('workspace.empty')}</p>
        <button type="button" onClick={() => void logout().then(onSignedOut)}>
          {t('workspace.signOut')}
        </button>
      </main>
    );
  }

  const favorites = allPages.filter((p) => p.favorite && !p.trashedAt);

  return (
    <div className="shell">
      <MobileDrawer
        open={mobileNavOpen}
        enabled={isMobile}
        onClose={() => setMobileNavOpen(false)}
        returnFocusRef={hamburgerRef}
        label={t('menu.label')}
        closeLabel={t('menu.close')}
      >
        {isMobile ? (
          <MobileMenu
            initials={initialsOf(user.name || user.email)}
            unread={unread}
            favorites={favorites}
            workspaces={workspaces}
            pagesByWorkspace={pagesByWs}
            collapsed={collapsed}
            activePageId={route.kind === 'page' ? route.id : null}
            onToggleWorkspace={(id) =>
              setCollapsed((current) => ({ ...current, [id]: !current[id] }))
            }
            onHome={() => {
              setMobileNavOpen(false);
              window.location.hash = '';
              setRoute({ kind: 'home' });
            }}
            onInbox={() => {
              setMobileNavOpen(false);
              setInboxOpen(true);
            }}
            onSearch={() => {
              setMobileNavOpen(false);
              setSearchOpen(true);
            }}
            onOpenPage={(id, newTab) => {
              setMobileNavOpen(false);
              if (newTab) openInNewTab(id);
              else openPage(id);
            }}
            onImport={() => {
              setMobileNavOpen(false);
              toast(t('toast.later.import'));
            }}
            onInvite={() => {
              setMobileNavOpen(false);
              setInviteOpen(true);
            }}
            onTrash={() => {
              setMobileNavOpen(false);
              setTrashOpen(true);
            }}
            onSettings={() => {
              setMobileNavOpen(false);
              setSettingsOpen(true);
            }}
            onNewWorkspace={() => {
              setMobileNavOpen(false);
              toast(t('toast.later.newWorkspace'));
            }}
            account={
              <AccountMenu
                email={user.email}
                preference={preference}
                open={openMenu === 'me'}
                onOpenChange={onMenuOpenChange('me')}
                onPreferenceChange={setPreference}
                onSignOut={() => void logout().then(onSignedOut)}
                rootClassName="mm-accountwrap"
                trigger={
                  <button type="button" className="mm-account">
                    <span className="mm-username">{user.name || user.email}</span>
                    <span className="mm-usermail">{user.email}</span>
                  </button>
                }
              />
            }
          />
        ) : (
          <nav
            className={sidebarClosed ? 'sidebar is-collapsed' : 'sidebar'}
            aria-label={t('workspace.sidebar')}
            aria-hidden={!isMobile && sidebarClosed ? true : undefined}
          >
            <div className="sidebar__brand">
              <Logo className="sidebar__logo" height={18} />
              <span className="sidebar__sp" />
              <button
                type="button"
                className="iconbtn sidebar__collapse"
                aria-label={t('sidebar.collapse')}
                title={t('sidebar.collapseHint')}
                onClick={() => setSidebarClosed(true)}
              >
                «
              </button>
            </div>

            <button
              type="button"
              className="row sidebar__search nb"
              onClick={() => setSearchOpen(true)}
            >
              <Glyph d={ICON.search} />
              <span style={{ flexGrow: 1 }}>{t('workspace.search')}</span>
              <span className="sidebar__hint">⌘K</span>
            </button>

            <button
              type="button"
              className="row sidebar__nav nb"
              onClick={() => {
                window.location.hash = '';
                setRoute({ kind: 'home' });
              }}
            >
              <span style={{ flexGrow: 1 }}>{t('sidebar.home')}</span>
            </button>
            <button
              type="button"
              className="row sidebar__nav nb"
              onClick={() => setInboxOpen(true)}
            >
              <span style={{ flexGrow: 1 }}>{t('sidebar.inbox')}</span>
              {unread > 0 ? <span className="sidebar__badge">{unread}</span> : null}
            </button>

            {favorites.length > 0 ? (
              <>
                <div className="sidebar__label">{t('sidebar.favorites')}</div>
                {favorites.map((fav) => (
                  <button
                    key={fav.id}
                    type="button"
                    className={
                      route.kind === 'page' && route.id === fav.id
                        ? 'row nb is-fav is-active'
                        : 'row nb is-fav'
                    }
                    onClick={(event) => {
                      if (event.metaKey || event.ctrlKey) openInNewTab(fav.id);
                      else openPage(fav.id);
                    }}
                  >
                    {fav.icon ? (
                      <span className="prow__icon">
                        <PageIcon icon={fav.icon} />
                      </span>
                    ) : (
                      <Glyph d={ICON.file} className="prow__glyph" />
                    )}
                    <span className="prow__title">{fav.title || t('page.untitled')}</span>
                  </button>
                ))}
              </>
            ) : null}

            <div className="sidebar__section">
              <span className="sidebar__label">{t('sidebar.workspaces')}</span>
              <button
                type="button"
                className="iconbtn iconbtn--sm"
                aria-label={t('sidebar.newWorkspace')}
                title={t('sidebar.newWorkspace')}
                onClick={() => toast(t('toast.later.newWorkspace'))}
              >
                <Glyph d={ICON.plus} />
              </button>
            </div>

            {workspaces.map((ws) => {
              const open = !collapsed[ws.id];
              const pages = (pagesByWs[ws.id] ?? []).filter((p) => !p.trashedAt);
              const roots = pages.filter((p) => p.parentId === null);
              const menuId = `ws:${ws.id}`;
              return (
                <div key={ws.id}>
                  <div
                    className={
                      dropTarget &&
                      'id' in dropTarget &&
                      dropTarget.pos === 'ws' &&
                      dropTarget.id === ws.id
                        ? 'wsr is-into'
                        : 'wsr'
                    }
                    onDragOver={(event) => {
                      if (!dragging) return;
                      event.preventDefault();
                      setDropTarget({ id: ws.id, pos: 'ws' });
                    }}
                    onDragLeave={() =>
                      setDropTarget((t2) => (t2 && 'pos' in t2 && t2.pos === 'ws' ? null : t2))
                    }
                    onDrop={(event) => {
                      event.preventDefault();
                      if (!dragging) return;
                      const moving = dragging;
                      setDragging(null);
                      setDropTarget(null);
                      void movePage(moving, null, null);
                    }}
                  >
                    <button
                      type="button"
                      className="wsr__chev nb"
                      aria-expanded={open}
                      aria-label={t('sidebar.toggleWorkspace')}
                      onClick={() => setCollapsed((c) => ({ ...c, [ws.id]: !c[ws.id] }))}
                    >
                      <Glyph d={ICON.chevron} />
                    </button>
                    <button
                      type="button"
                      className={route.kind === 'home' ? 'wsr__home is-active' : 'wsr__home'}
                      title={t('sidebar.openHome')}
                      onClick={() => {
                        window.location.hash = '';
                        setRoute({ kind: 'home' });
                      }}
                    >
                      <span className="wsr__badge">
                        {(ws.name || '?').slice(0, 1).toUpperCase()}
                      </span>
                      <span className="wsr__name">{ws.name}</span>
                    </button>
                    <div className={openMenu === menuId ? 'hov is-open' : 'hov'}>
                      <button
                        type="button"
                        className="iconbtn"
                        aria-label={t('sidebar.newPageIn', { workspace: ws.name })}
                        title={t('workspace.newPage')}
                        onClick={() => void onCreate(ws.id, null)}
                      >
                        <Glyph d={ICON.plus} />
                      </button>
                      <WorkspaceMenu
                        workspace={ws}
                        open={openMenu === menuId}
                        onOpenChange={onMenuOpenChange(menuId)}
                        onInvite={() => setInviteOpen(true)}
                        onCreatePage={() => void onCreate(ws.id, null)}
                        onSettings={() => setSettingsOpen(true)}
                        onCopyLink={() =>
                          void navigator.clipboard
                            ?.writeText(`${window.location.origin}/${ws.slug}`)
                            .then(() => toast(t('toast.wsLinkCopied')))
                            .catch(() => toast(t('error.generic')))
                        }
                      />
                    </div>
                  </div>
                  {open ? (
                    <div className="tree">
                      <PageTree
                        pages={pages}
                        roots={roots}
                        activeId={route.kind === 'page' ? route.id : null}
                        dragging={dragging}
                        dropTarget={dropTarget}
                        openMenu={openMenu}
                        setOpenMenu={setOpenMenu}
                        onOpen={openPage}
                        onCreate={(parentId) => void onCreate(ws.id, parentId)}
                        onDragStart={setDragging}
                        onDropTarget={setDropTarget}
                        onMove={(pageId, parentId, beforeId) =>
                          void movePage(pageId, parentId, beforeId)
                        }
                        onRename={rename}
                        onDuplicate={(p) => void onDuplicate(p)}
                        onCopyLink={(p) => copyLink(p.id, t('toast.linkCopied'))}
                        onToggleFavorite={(p) => void onToggleFavorite(p)}
                        onOpenInNewTab={openInNewTab}
                        onTrash={(p) => void onTrashPage(p)}
                        onAddComment={(pageId) => {
                          openPage(pageId);
                          setPageComposeOpen(true);
                        }}
                      />
                    </div>
                  ) : null}
                </div>
              );
            })}

            <div style={{ flexGrow: 1 }} />
            <button
              type="button"
              className="row sidebar__nav nb"
              onClick={() => toast(t('toast.later.import'))}
            >
              <span style={{ flexGrow: 1 }}>{t('sidebar.import')}</span>
            </button>
            <button
              type="button"
              className="row sidebar__nav nb"
              onClick={() => setTrashOpen(true)}
            >
              <span style={{ flexGrow: 1 }}>{t('sidebar.trash')}</span>
            </button>
            <button
              type="button"
              className="row sidebar__nav nb"
              onClick={() => setSettingsOpen(true)}
            >
              <span style={{ flexGrow: 1 }}>{t('sidebar.settings')}</span>
            </button>
            <button
              type="button"
              className="row sidebar__nav nb"
              onClick={() => setInviteOpen(true)}
            >
              <span style={{ flexGrow: 1 }}>{t('sidebar.inviteMembers')}</span>
            </button>

            <AccountMenu
              email={user.email}
              preference={preference}
              open={openMenu === 'me'}
              onOpenChange={onMenuOpenChange('me')}
              onPreferenceChange={setPreference}
              onSignOut={() => void logout().then(onSignedOut)}
              trigger={
                <button type="button" className="sidebar__foot nb">
                  <span className="sidebar__avatar" aria-hidden="true">
                    {initialsOf(user.name || user.email)}
                  </span>
                  <span className="sidebar__me">{user.name || user.email}</span>
                </button>
              }
            />
          </nav>
        )}
      </MobileDrawer>

      <div className="main">
        {isMobile ? null : (
          <div
            className="tabstrip"
            role="tablist"
            aria-label={t('tab.list')}
            onKeyDown={onTabListKey}
          >
            {tabs.map((tab) => {
              const summary = tab.pageId
                ? allPages.find((item) => item.id === tab.pageId)
                : undefined;
              const icon = summary ? pageIcon(summary.icon) : null;
              const label = !tab.pageId
                ? t('tab.new')
                : summary
                  ? summary.title || t('page.untitled')
                  : allPages.length === 0
                    ? t('page.untitled')
                    : t('tab.missing');
              const selected = tab.id === activeTabId;
              return (
                <div key={tab.id} className={selected ? 'tab is-active' : 'tab'}>
                  <button
                    type="button"
                    className="tab__btn nb"
                    role="tab"
                    aria-selected={selected}
                    onClick={() => switchTo(tab.id)}
                  >
                    {icon ? (
                      <span className="tab__icon">
                        <PageIcon icon={icon} />
                      </span>
                    ) : null}
                    <span className="tab__label">{label}</span>
                  </button>
                  {tabs.length > 1 ? (
                    <button
                      type="button"
                      className="tab__close nb"
                      aria-label={t('tab.close')}
                      onClick={() => onCloseTab(tab.id)}
                    >
                      ×
                    </button>
                  ) : null}
                </div>
              );
            })}
            <button
              type="button"
              className="tab__new nb"
              aria-label={t('tab.new')}
              title={t('tab.new')}
              onClick={() => openInNewTab(null)}
            >
              +
            </button>
          </div>
        )}
        <header className="topbar">
          {isMobile ? (
            <button
              ref={hamburgerRef}
              type="button"
              className="iconbtn topbar__menu"
              aria-label={mobileNavOpen ? t('topbar.closeMenu') : t('topbar.openMenu')}
              aria-haspopup="dialog"
              aria-expanded={mobileNavOpen}
              aria-controls="mobile-navigation"
              onClick={() => setMobileNavOpen((open) => !open)}
            >
              <Glyph d={ICON.menu} size={20} />
            </button>
          ) : null}
          {!isMobile && sidebarClosed ? (
            <button
              type="button"
              className="iconbtn"
              aria-label={t('sidebar.expand')}
              title={t('sidebar.expandHint')}
              onClick={() => setSidebarClosed(false)}
            >
              »
            </button>
          ) : null}
          <button
            type="button"
            className="iconbtn topbar__nav"
            aria-label={t('topbar.back')}
            title={t('topbar.back')}
            disabled={!activeTab?.back.length}
            onClick={onBack}
          >
            <Glyph d={ICON.back} />
          </button>
          <button
            type="button"
            className="iconbtn topbar__nav topbar__forward"
            aria-label={t('topbar.forward')}
            title={t('topbar.forward')}
            disabled={!activeTab?.fwd.length}
            onClick={onForward}
          >
            <Glyph d={ICON.forward} />
          </button>
          {isMobile && currentCrumb ? (
            <ActionPopover
              open={crumbsOpen}
              onOpenChange={setCrumbsOpen}
              label={t('topbar.breadcrumbs')}
              className="crumb-sheet"
              rootClassName="topbar__titlewrap"
              side="bottom"
              align="start"
              trigger={
                <button type="button" className="crumb nb topbar__title">
                  {currentCrumb.icon ? (
                    <span className="crumb__icon">
                      <PageIcon icon={currentCrumb.icon} />
                    </span>
                  ) : null}
                  <span className="crumb__label">{currentCrumb.label}</span>
                </button>
              }
            >
              <nav aria-label={t('topbar.breadcrumbs')}>
                {crumbs.map((crumb) => (
                  <button
                    key={crumb.key}
                    type="button"
                    className={
                      crumb.current ? 'crumb-sheet__item is-current nb' : 'crumb-sheet__item nb'
                    }
                    aria-current={crumb.current ? 'page' : undefined}
                    onClick={() => {
                      setCrumbsOpen(false);
                      if (!crumb.current) crumb.onOpen();
                    }}
                  >
                    {crumb.icon ? (
                      <span className="crumb__icon">
                        <PageIcon icon={crumb.icon} />
                      </span>
                    ) : null}
                    <span className="crumb__label">{crumb.label}</span>
                  </button>
                ))}
              </nav>
            </ActionPopover>
          ) : null}
          {!isMobile && crumbs.length > 0 ? (
            <nav className="topbar__crumbs" aria-label={t('topbar.breadcrumbs')}>
              {crumbs.map((crumb) => (
                <span key={crumb.key} className="topbar__pair">
                  <span className="topbar__sep" aria-hidden="true">
                    /
                  </span>
                  {crumb.current ? (
                    <span className="crumb is-current">
                      {crumb.icon ? (
                        <span className="crumb__icon">
                          <PageIcon icon={crumb.icon} />
                        </span>
                      ) : null}
                      <span className="crumb__label">{crumb.label}</span>
                    </span>
                  ) : (
                    <button type="button" className="crumb nb" onClick={crumb.onOpen}>
                      {crumb.icon ? (
                        <span className="crumb__icon">
                          <PageIcon icon={crumb.icon} />
                        </span>
                      ) : null}
                      <span className="crumb__label">{crumb.label}</span>
                    </button>
                  )}
                </span>
              ))}
            </nav>
          ) : null}
          {page?.isLocked ? <span className="topbar__lock">{t('page.locked')}</span> : null}
          <div className="topbar__spacer" />
          {page ? (
            <div className="topbar__presence" data-menu-root aria-label={t('presence.label')}>
              {otherViewers.map((viewer) => {
                const menuId = `presence:${viewer.userId}`;
                return (
                  <span className="topbar__person" key={viewer.userId}>
                    <button
                      type="button"
                      className="topbar__presenceBtn nb"
                      aria-label={`${viewer.name}, ${t('presence.lastViewed', {
                        when: timeAgo(viewer.viewedAt),
                      })}`}
                      aria-expanded={openMenu === menuId}
                      onClick={() => setOpenMenu((menu) => (menu === menuId ? null : menuId))}
                    >
                      <span
                        className="cmt-avatar cmt-avatar--26 topbar__avatar topbar__avatar--other is-away"
                        aria-hidden="true"
                      >
                        {initialsOf(viewer.name)}
                      </span>
                    </button>
                    {openMenu === menuId ? (
                      <span className="presence__tip" role="dialog">
                        <span className="presence__tip-name">{viewer.name}</span>
                        <span className="presence__tip-sub">
                          {t('presence.lastViewed', { when: timeAgo(viewer.viewedAt) })}
                        </span>
                      </span>
                    ) : null}
                  </span>
                );
              })}
              <span className="topbar__person">
                <button
                  type="button"
                  className="topbar__presenceBtn nb"
                  aria-label={`${t('presence.you', { name: user.name || user.email })}, ${t('presence.viewingNow')}`}
                  aria-expanded={openMenu === 'presence-me'}
                  onClick={() =>
                    setOpenMenu((menu) => (menu === 'presence-me' ? null : 'presence-me'))
                  }
                >
                  <span className="cmt-avatar cmt-avatar--26 topbar__avatar" aria-hidden="true">
                    {initialsOf(user.name || user.email)}
                  </span>
                </button>
                {openMenu === 'presence-me' ? (
                  <span className="presence__tip" role="dialog">
                    <span className="presence__tip-name">
                      {t('presence.you', { name: user.name || user.email })}
                    </span>
                    <span className="presence__tip-sub">{t('presence.viewingNow')}</span>
                  </span>
                ) : null}
              </span>
            </div>
          ) : null}
          {page && threads.length > 0 ? (
            <div className="topbar__comments" data-menu-root>
              <button
                type="button"
                className={
                  commentsHidden ? 'topbar__commentsMain nb' : 'topbar__commentsMain nb is-on'
                }
                aria-pressed={!commentsHidden}
                aria-label={t('comments.title')}
                onClick={() => setCommentsHidden((hidden) => !hidden)}
              >
                <Glyph d={ICON.comment} size={18} />
                <span>{openThreadCount}</span>
              </button>
              <button
                type="button"
                className="topbar__commentsChevron nb"
                aria-label={t('comments.displayOptions')}
                aria-haspopup="menu"
                aria-expanded={openMenu === 'comments'}
                onClick={() => setOpenMenu((menu) => (menu === 'comments' ? null : 'comments'))}
              >
                <Glyph d="m6 9 6 6 6-6" size={14} />
              </button>
              {openMenu === 'comments' ? (
                <div
                  className="menu menu--comments"
                  role="menu"
                  aria-label={t('comments.displayOptions')}
                >
                  <button
                    type="button"
                    className="menu__item nb"
                    role="menuitemcheckbox"
                    aria-checked={showResolved}
                    onClick={() => setShowResolved((shown) => !shown)}
                  >
                    <span
                      className={showResolved ? 'menu__check is-on' : 'menu__check'}
                      aria-hidden="true"
                    >
                      {showResolved ? <Glyph d="M20 6 9 17l-5-5" size={12} /> : null}
                    </span>
                    {t('comments.showResolved', { count: String(resolvedThreadCount) })}
                  </button>
                  <label className="menu__label" htmlFor="cmt-author-filter">
                    {t('comments.author')}
                  </label>
                  <select
                    id="cmt-author-filter"
                    className="menu__select"
                    value={authorFilter}
                    onChange={(event) => setAuthorFilter(event.target.value)}
                  >
                    <option value="all">{t('comments.allAuthors')}</option>
                    {threadAuthors.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
            </div>
          ) : null}
          {page ? (
            <button
              type="button"
              className="btn-prim topbar__share"
              onClick={() => setShareOpen(true)}
            >
              {t('topbar.share')}
            </button>
          ) : null}
          {page ? (
            <button
              type="button"
              className={page.favorite ? 'iconbtn topbar__star is-on' : 'iconbtn topbar__star'}
              aria-pressed={page.favorite}
              aria-label={page.favorite ? t('page.menu.unfavorite') : t('page.menu.favorite')}
              onClick={() => void onToggleFavorite(page)}
            >
              <Glyph d={ICON.star} size={18} />
            </button>
          ) : null}
          {page && currentSummary ? (
            <DocumentMenu
              page={page}
              open={openMenu === 'page'}
              onOpenChange={onMenuOpenChange('page')}
              onPatch={async (patch) => {
                try {
                  const updated = await updatePage(page.id, patch);
                  setPage((current) =>
                    current && current.id === page.id
                      ? {
                          ...current,
                          ...updated,
                          ...(patch.isLocked !== undefined ? { isLocked: patch.isLocked } : {}),
                          ...(patch.font !== undefined ? { font: patch.font } : {}),
                          ...(patch.small !== undefined ? { small: patch.small } : {}),
                          ...(patch.wide !== undefined ? { wide: patch.wide } : {}),
                        }
                      : current,
                  );
                } catch {
                  setError(t('error.generic'));
                }
              }}
              onToggleFavorite={() => void onToggleFavorite(page)}
              onDuplicate={() => void onDuplicate(page)}
              onTrash={() => void onTrashPage(page)}
              onAddComment={() => setPageComposeOpen(true)}
              onUndo={() => pageActions.current?.undo()}
              onRedo={() => pageActions.current?.redo()}
              onImport={() => setMarkdown({ mode: 'import', text: '' })}
              onExport={() =>
                setMarkdown({
                  mode: 'export',
                  text: pageActions.current?.toMarkdown() ?? '',
                })
              }
            />
          ) : null}
        </header>
        <main className={page ? 'document' : 'document document--blank'}>
          {error ? (
            <p className="banner" role="alert">
              {error}
            </p>
          ) : null}
          {route.kind === 'legal' ? (
            <LegalPage page={route.page} />
          ) : page ? (
            <PageView
              page={page}
              focusTitleNonce={focusTitleNonce}
              comments={editorComments}
              actionsRef={pageActions}
              onOpenRow={showPage}
              onChange={async (patch) => {
                const updated = await updatePage(page.id, patch);
                setPage({ ...page, ...updated });
                refresh();
              }}
            />
          ) : (
            <div className="document__empty">
              <span className="document__empty-title">{t('workspace.selectPage')}</span>
              <span>{t('workspace.selectPageHint')}</span>
              <button
                type="button"
                className="btn-prim"
                onClick={() => void onCreate(primary.id, null)}
              >
                {t('workspace.newPage')}
              </button>
            </div>
          )}
        </main>
      </div>

      <CookieNotice />

      {markdown ? (
        <MarkdownDialog
          mode={markdown.mode}
          text={markdown.text}
          locked={page?.isLocked ?? false}
          onClose={() => setMarkdown(null)}
          onImport={(text) => {
            const applied = pageActions.current?.replaceFromMarkdown(text) ?? false;
            if (!applied) {
              toast(t('page.menu.lockedHint'));
              return;
            }
            setMarkdown(null);
          }}
        />
      ) : null}
      {inviteOpen ? (
        <InviteDialog
          workspaceName={primary.name}
          onClose={() => setInviteOpen(false)}
          onSubmit={async (email, role) => {
            await inviteToWorkspace(primary.id, email, role);
          }}
        />
      ) : null}
      {inboxOpen ? (
        <InboxPanel
          viewerName={user.name ?? user.email}
          onClose={() => setInboxOpen(false)}
          onOpenPage={(id) => {
            setInboxOpen(false);
            openPage(id);
          }}
          onUnreadChange={setUnread}
        />
      ) : null}
      {shareOpen && page ? (
        <ShareDialog
          pageId={page.id}
          title={
            page.icon && !page.icon.startsWith('file:') ? `${page.icon} ${page.title}` : page.title
          }
          link={`${window.location.origin}${window.location.pathname}#p/${page.id}`}
          onClose={() => setShareOpen(false)}
          onInviteMembers={() => {
            setShareOpen(false);
            setInviteOpen(true);
          }}
        />
      ) : null}
      {settingsOpen ? (
        <SettingsDialog
          workspace={primary}
          address={`${window.location.origin}/${primary.slug}`}
          onClose={() => setSettingsOpen(false)}
          onSaved={() => refresh()}
          onError={() => toast(t('error.generic'))}
        />
      ) : null}
      {searchOpen ? (
        <SearchDialog
          workspaceId={primary.id}
          onClose={() => setSearchOpen(false)}
          onOpen={(id) => {
            setSearchOpen(false);
            openPage(id);
          }}
        />
      ) : null}
      {trashOpen ? (
        <TrashDialog
          workspaceId={primary.id}
          onClose={() => setTrashOpen(false)}
          onChanged={() => refresh()}
        />
      ) : null}
    </div>
  );
}

function PageTree({
  pages,
  roots,
  activeId,
  dragging,
  dropTarget,
  openMenu,
  setOpenMenu,
  onOpen,
  onCreate,
  onDragStart,
  onDropTarget,
  onMove,
  onRename,
  onDuplicate,
  onCopyLink,
  onToggleFavorite,
  onOpenInNewTab,
  onTrash,
  onAddComment,
}: {
  pages: PageSummary[];
  roots: PageSummary[];
  activeId: string | null;
  dragging: string | null;
  dropTarget: { id: string; pos: string } | null;
  openMenu: string | null;
  setOpenMenu: React.Dispatch<React.SetStateAction<string | null>>;
  onOpen: (id: string) => void;
  onCreate: (parentId: string) => void;
  onDragStart: (id: string | null) => void;
  onDropTarget: (
    t: { id: string; pos: 'before' | 'after' | 'in' } | { id: string; pos: 'ws' } | null,
  ) => void;
  onMove: (pageId: string, parentId: string | null, beforeId: string | null) => void;
  onRename: (id: string) => void;
  onDuplicate: (page: PageSummary) => void;
  onCopyLink: (page: PageSummary) => void;
  onToggleFavorite: (page: PageSummary) => void;
  onOpenInNewTab: (pageId: string) => void;
  onTrash: (page: PageSummary) => void | Promise<void>;
  onAddComment: (pageId: string) => void;
}): React.ReactElement {
  return (
    <>
      {roots.map((node) => (
        <PageRow
          key={node.id}
          page={node}
          pages={pages}
          depth={0}
          activeId={activeId}
          dragging={dragging}
          dropTarget={dropTarget}
          openMenu={openMenu}
          setOpenMenu={setOpenMenu}
          onOpen={onOpen}
          onCreate={onCreate}
          onDragStart={onDragStart}
          onDropTarget={onDropTarget}
          onMove={onMove}
          onRename={onRename}
          onDuplicate={onDuplicate}
          onCopyLink={onCopyLink}
          onToggleFavorite={onToggleFavorite}
          onOpenInNewTab={onOpenInNewTab}
          onTrash={onTrash}
          onAddComment={onAddComment}
        />
      ))}
    </>
  );
}

function PageRow({
  page: node,
  pages,
  depth,
  activeId,
  dragging,
  dropTarget,
  openMenu,
  setOpenMenu,
  onOpen,
  onCreate,
  onDragStart,
  onDropTarget,
  onMove,
  onRename,
  onDuplicate,
  onCopyLink,
  onToggleFavorite,
  onOpenInNewTab,
  onTrash,
  onAddComment,
}: {
  page: PageSummary;
  pages: PageSummary[];
  depth: number;
  activeId: string | null;
  dragging: string | null;
  dropTarget: { id: string; pos: string } | null;
  openMenu: string | null;
  setOpenMenu: React.Dispatch<React.SetStateAction<string | null>>;
  onOpen: (id: string) => void;
  onCreate: (parentId: string) => void;
  onDragStart: (id: string | null) => void;
  onDropTarget: (
    t: { id: string; pos: 'before' | 'after' | 'in' } | { id: string; pos: 'ws' } | null,
  ) => void;
  onMove: (pageId: string, parentId: string | null, beforeId: string | null) => void;
  onRename: (id: string) => void;
  onDuplicate: (page: PageSummary) => void;
  onCopyLink: (page: PageSummary) => void;
  onToggleFavorite: (page: PageSummary) => void;
  onOpenInNewTab: (pageId: string) => void;
  onTrash: (page: PageSummary) => void | Promise<void>;
  onAddComment: (pageId: string) => void;
}): React.ReactElement {
  const children = pages.filter((p) => p.parentId === node.id).sort(byPosition);
  const siblings = pages.filter((p) => p.parentId === node.parentId).sort(byPosition);
  const index = siblings.findIndex((p) => p.id === node.id);
  const isActive = node.id === activeId;
  const menuId = `p:${node.id}`;
  const dropHere = dropTarget && dropTarget.id === node.id ? dropTarget.pos : null;
  const [pageClosed, setPageClosed] = useState(false);

  return (
    <>
      <div
        className={`prow${children.length > 0 ? ' prow--branch' : ''}${
          dropHere === 'before' ? ' is-before' : ''
        }${dropHere === 'after' ? ' is-after' : ''}${dropHere === 'in' ? ' is-into' : ''}`}
        style={{ marginLeft: depth * 12 }}
        draggable
        onDragStart={(event) => {
          event.dataTransfer.setData('text/plain', node.id);
          event.dataTransfer.effectAllowed = 'move';
          onDragStart(node.id);
        }}
        onDragEnd={() => {
          onDragStart(null);
          onDropTarget(null);
        }}
        onDragOver={(event) => {
          if (!dragging || dragging === node.id) return;
          event.preventDefault();
          const rect = event.currentTarget.getBoundingClientRect();
          const ratio = (event.clientY - rect.top) / rect.height;
          const pos = ratio < 0.28 ? 'before' : ratio > 0.72 ? 'after' : 'in';
          setDropHereSafe(onDropTarget, node.id, pos);
        }}
        onDrop={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (!dragging || dragging === node.id) return;
          const moving = dragging;
          const rect = event.currentTarget.getBoundingClientRect();
          const ratio = (event.clientY - rect.top) / rect.height;
          const pos = ratio < 0.28 ? 'before' : ratio > 0.72 ? 'after' : 'in';
          onDragStart(null);
          onDropTarget(null);
          if (pos === 'in') {
            onMove(moving, node.id, null);
          } else {
            const next = pos === 'before' ? node : siblings[index + 1];
            onMove(moving, node.parentId, next ? next.id : null);
          }
        }}
      >
        {children.length > 0 ? (
          <button
            type="button"
            className="prow__chev nb"
            aria-expanded={!pageClosed}
            aria-label={t('sidebar.togglePage')}
            onClick={() => setPageClosed((closed) => !closed)}
          >
            <Glyph d={ICON.chevron} size={14} />
          </button>
        ) : null}
        <button
          type="button"
          className={isActive ? 'prow__link is-active' : 'prow__link'}
          aria-current={isActive ? 'page' : undefined}
          onClick={(event) => {
            if (event.metaKey || event.ctrlKey) onOpenInNewTab(node.id);
            else onOpen(node.id);
          }}
        >
          {node.icon ? (
            <span className="prow__icon">
              <PageIcon icon={node.icon} />
            </span>
          ) : (
            <Glyph d={ICON.file} className="prow__glyph" />
          )}
          <span className="prow__title">{node.title || t('page.untitled')}</span>
        </button>
        <div className={openMenu === menuId ? 'hov is-open' : 'hov'}>
          <button
            type="button"
            className="iconbtn"
            aria-label={t('sidebar.addSubpage')}
            title={t('sidebar.addSubpage')}
            onClick={() => onCreate(node.id)}
          >
            <Glyph d={ICON.plus} size={14} />
          </button>
          <PageActionMenu
            page={node}
            open={openMenu === menuId}
            onOpenChange={(open) =>
              setOpenMenu((current) => (open ? menuId : current === menuId ? null : current))
            }
            onToggleFavorite={onToggleFavorite}
            onOpenInNewTab={onOpenInNewTab}
            onRename={onRename}
            onDuplicate={onDuplicate}
            onCopyLink={onCopyLink}
            onTrash={(target) => void onTrash(target)}
            onAddComment={() => onAddComment(node.id)}
          />
        </div>
      </div>
      {!pageClosed &&
        children.map((child) => (
          <PageRow
            key={child.id}
            page={child}
            pages={pages}
            depth={depth + 1}
            activeId={activeId}
            dragging={dragging}
            dropTarget={dropTarget}
            openMenu={openMenu}
            setOpenMenu={setOpenMenu}
            onOpen={onOpen}
            onCreate={onCreate}
            onDragStart={onDragStart}
            onDropTarget={onDropTarget}
            onMove={onMove}
            onRename={onRename}
            onDuplicate={onDuplicate}
            onCopyLink={onCopyLink}
            onToggleFavorite={onToggleFavorite}
            onOpenInNewTab={onOpenInNewTab}
            onTrash={onTrash}
            onAddComment={onAddComment}
          />
        ))}
    </>
  );
}

function setDropHereSafe(
  onDropTarget: (
    t: { id: string; pos: 'before' | 'after' | 'in' } | { id: string; pos: 'ws' } | null,
  ) => void,
  id: string,
  pos: 'before' | 'after' | 'in',
): void {
  onDropTarget({ id, pos });
}

function pageClassName(page: PageDetail, showThreads: boolean): string {
  const names = ['page'];
  if (page.font === 1) names.push('page--serif');
  if (page.font === 2) names.push('page--mono');
  if (page.small) names.push('page--small');
  if (page.wide) names.push('page--wide');
  if (showThreads) names.push('page--threads');
  return names.join(' ');
}

export function PageView({
  page,
  focusTitleNonce,
  comments,
  actionsRef,
  onOpenRow,
  onChange,
}: {
  page: PageDetail;
  focusTitleNonce: number;
  comments: PageEditorComments;
  actionsRef: React.RefObject<PageEditorActions | null>;
  onOpenRow: (rowId: string) => void;
  onChange: (patch: {
    title?: string;
    icon?: string | null;
    cover?: PageCover | null;
  }) => Promise<void>;
}): React.ReactElement {
  const toast = useToast();
  const [title, setTitle] = useState(page.title);
  const [iconOpen, setIconOpen] = useState(false);
  const [coverOpen, setCoverOpen] = useState(false);
  const [iconMode, setIconMode] = useState<'emoji' | 'upload'>('emoji');
  const [focusIcon, setFocusIcon] = useState(false);
  const titleRef = useRef<HTMLTextAreaElement>(null);
  const iconButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => setTitle(page.title), [page.id, page.title]);
  useEffect(() => {
    if (focusTitleNonce > 0) titleRef.current?.focus();
  }, [focusTitleNonce, page.id]);
  useEffect(() => {
    // A textarea so the title can wrap on a narrow screen instead of being cut off.
    const element = titleRef.current;
    if (!element) return;
    element.style.height = 'auto';
    element.style.height = `${element.scrollHeight}px`;
  }, [title, page.id]);
  useEffect(() => {
    setIconOpen(false);
    setCoverOpen(false);
    setFocusIcon(false);
  }, [page.id]);
  useEffect(() => {
    // Choosing the first icon replaces the "add icon" button, so focus has to move to the new one.
    if (!focusIcon || !page.icon) return;
    iconButtonRef.current?.focus();
    setFocusIcon(false);
  }, [focusIcon, page.icon]);

  const icon = pageIcon(page.icon);
  const cover = COVERS.find((item) => item.id === page.cover);
  const imageCover = page.cover?.startsWith('file:') ? page.cover.slice(5) : null;
  const uploadCover = async (file: File): Promise<void> => {
    try {
      const uploaded = await uploadFile(page.id, file);
      await onChange({ cover: `file:${uploaded.id}` });
      setCoverOpen(false);
    } catch {
      toast(t('media.uploadFailed'));
    }
  };
  const toolsOpen = iconOpen || coverOpen ? ' pghead__add is-open' : ' pghead__add';

  const iconPopover = (trigger: React.ReactElement): React.ReactElement => (
    <ActionPopover
      open={iconOpen}
      onOpenChange={(next) => {
        if (next) setCoverOpen(false);
        setIconOpen(next);
      }}
      label={t('page.icon.choose')}
      className="picker picker--icon"
      rootClassName="popover-root"
      onCloseAutoFocus={(event) => {
        // The trigger may be replaced by the icon button once the choice is saved. When the
        // cover picker took over, returning focus here would dismiss it again.
        if (focusIcon || coverOpen) event.preventDefault();
      }}
      trigger={trigger}
    >
      <div className="picker__tabs">
        <button
          type="button"
          className={iconMode === 'emoji' ? 'picker__tab is-on nb' : 'picker__tab nb'}
          onClick={() => setIconMode('emoji')}
        >
          {t('page.icon.emoji')}
        </button>
        <button
          type="button"
          className={iconMode === 'upload' ? 'picker__tab is-on nb' : 'picker__tab nb'}
          onClick={() => setIconMode('upload')}
        >
          {t('page.icon.upload')}
        </button>
        <span className="picker__spacer" />
        <button
          type="button"
          className="picker__quiet nb"
          onClick={() => {
            void onChange({ icon: null });
            setIconOpen(false);
          }}
        >
          {t('page.icon.remove')}
        </button>
      </div>
      {iconMode === 'emoji' ? (
        <EmojiPicker
          onPick={(emoji) => {
            void onChange({ icon: emoji });
            setIconOpen(false);
          }}
        />
      ) : (
        <div className="picker__upload">
          <input
            type="file"
            accept="image/png,image/svg+xml,image/jpeg,image/webp"
            aria-label={t('page.icon.upload')}
            onChange={() => toast(t('toast.later.iconUpload'))}
          />
          <p>{t('page.icon.uploadHint')}</p>
        </div>
      )}
    </ActionPopover>
  );

  return (
    <>
      {imageCover ? (
        <div className="page__cover">
          <img className="page__cover-image" src={mediaUrl(imageCover)} alt="" />
        </div>
      ) : cover ? (
        <div className={`page__cover ${cover.className}`} />
      ) : null}
      <article
        className={pageClassName(
          page,
          !comments.hidden && comments.threads.some((thread) => thread.quote),
        )}
      >
        <div className="pghead">
          <div className="pghead__tools">
            {icon
              ? null
              : iconPopover(
                  <button type="button" className={`nb row${toolsOpen}`}>
                    {t('page.icon.add')}
                  </button>,
                )}
            <ActionPopover
              open={coverOpen}
              onOpenChange={(next) => {
                if (next) setIconOpen(false);
                setCoverOpen(next);
              }}
              label={t('page.cover.choose')}
              className="picker picker--cover"
              rootClassName="popover-root"
              onCloseAutoFocus={(event) => {
                // Same as the icon picker: do not pull focus away from the picker that replaced it.
                if (iconOpen) event.preventDefault();
              }}
              trigger={
                <button type="button" className={`nb row${toolsOpen}`}>
                  {t(cover || imageCover ? 'page.cover.change' : 'page.cover.add')}
                </button>
              }
            >
              <label
                className="picker__drop"
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => {
                  event.preventDefault();
                  const file = event.dataTransfer.files[0];
                  if (file) void uploadCover(file);
                }}
              >
                <input
                  type="file"
                  accept="image/*"
                  aria-label={t('page.cover.upload')}
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) void uploadCover(file);
                  }}
                />
                <Glyph d={ICON.upload} className="picker__drop-icon" />
                <span className="picker__drop-title">{t('page.cover.upload')}</span>
                <span className="picker__drop-hint">{t('page.cover.uploadHint')}</span>
              </label>
              <p className="picker__hint">{t('page.cover.orColor')}</p>
              <div className="picker__swatches">
                {COVERS.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className={`picker__swatch ${item.className} nb`}
                    aria-label={t(item.label)}
                    title={t(item.label)}
                    aria-pressed={page.cover === item.id}
                    onClick={() => {
                      void onChange({ cover: item.id });
                      setCoverOpen(false);
                    }}
                  />
                ))}
              </div>
              <div className="picker__foot">
                <button
                  type="button"
                  className="picker__quiet nb"
                  onClick={() => {
                    void onChange({ cover: null });
                    setCoverOpen(false);
                  }}
                >
                  {t('page.cover.remove')}
                </button>
              </div>
            </ActionPopover>
          </div>
          <div className="pghead__row">
            {icon
              ? iconPopover(
                  <button
                    ref={iconButtonRef}
                    type="button"
                    className="pghead__icon nb"
                    aria-label={t('page.icon.change')}
                  >
                    <PageIcon icon={icon} />
                  </button>,
                )
              : null}
            <textarea
              ref={titleRef}
              id="page-title"
              className="page__title"
              aria-label={t('page.title')}
              placeholder={t('page.untitled')}
              enterKeyHint="done"
              rows={1}
              value={title}
              readOnly={page.isLocked}
              onChange={(event) => {
                setTitle(event.target.value);
                event.target.style.height = 'auto';
                event.target.style.height = `${event.target.scrollHeight}px`;
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  event.currentTarget.blur();
                }
              }}
              onBlur={() => {
                if (title !== page.title) void onChange({ title });
              }}
            />
          </div>
        </div>
        {page.parentPageId && page.parentId ? (
          <DatabaseRowProperties
            key={`properties:${page.id}`}
            dataSourceId={page.parentId}
            rowId={page.id}
            locked={page.isLocked}
            people={comments.members}
            onOpenRow={onOpenRow}
          />
        ) : null}
        <PageEditor
          key={page.id}
          pageId={page.id}
          workspaceId={page.workspaceId}
          version={page.document.version}
          content={page.document.content as JSONContent}
          comments={comments}
          locked={page.isLocked}
          actionsRef={actionsRef}
          onOpenRow={onOpenRow}
        />
      </article>
    </>
  );
}

function MarkdownDialog({
  mode,
  text,
  locked,
  onClose,
  onImport,
}: {
  mode: 'import' | 'export';
  text: string;
  locked: boolean;
  onClose: () => void;
  onImport: (text: string) => void;
}): React.ReactElement {
  const titleId = useId();
  const toast = useToast();
  const areaRef = useRef<HTMLTextAreaElement>(null);
  const [value, setValue] = useState(text);
  const title = mode === 'import' ? t('page.markdown.importTitle') : t('page.markdown.exportTitle');

  useEffect(() => {
    areaRef.current?.focus();
  }, []);

  return (
    <div
      className="overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={(event) => {
          if (event.key === 'Escape') onClose();
        }}
      >
        <h2 id={titleId}>{title}</h2>
        <p>{t('page.markdown.hint')}</p>
        <textarea
          ref={areaRef}
          className="dialog__markdown"
          aria-label={title}
          value={value}
          readOnly={mode === 'export'}
          onChange={(event) => setValue(event.target.value)}
        />
        <div className="dialog__actions">
          <button type="button" onClick={onClose}>
            {t('page.markdown.close')}
          </button>
          {mode === 'export' ? (
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard.writeText(value).then(() => {
                  toast(t('toast.markdownCopied'));
                });
              }}
            >
              {t('page.markdown.copy')}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                if (locked) {
                  toast(t('page.menu.lockedHint'));
                  return;
                }
                onImport(value);
              }}
            >
              {t('page.markdown.import')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function InviteDialog({
  workspaceName,
  onClose,
  onSubmit,
}: {
  workspaceName: string;
  onClose: () => void;
  onSubmit: (email: string, role: 'admin' | 'member' | 'guest') => Promise<void>;
}): React.ReactElement {
  const titleId = useId();
  const emailRef = useRef<HTMLInputElement>(null);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'member' | 'guest'>('member');
  const [sent, setSent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    emailRef.current?.focus();
  }, []);

  return (
    <div
      className="overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={(event) => {
          if (event.key === 'Escape') onClose();
        }}
      >
        <h2 id={titleId}>{t('invite.title', { workspace: workspaceName })}</h2>
        {sent ? <p role="status">{t('invite.sent', { email: sent })}</p> : null}
        {error ? <p role="alert">{error}</p> : null}
        <form
          className="dialog__form"
          onSubmit={(event) => {
            event.preventDefault();
            setError(null);
            void onSubmit(email, role)
              .then(() => {
                setSent(email);
                setEmail('');
              })
              .catch(() => setError(t('error.generic')));
          }}
        >
          <label>
            {t('invite.email')}
            <input
              ref={emailRef}
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </label>
          <label>
            {t('invite.role')}
            <select
              value={role}
              onChange={(event) => setRole(event.target.value as 'admin' | 'member' | 'guest')}
            >
              <option value="admin">{t('invite.role.admin')}</option>
              <option value="member">{t('invite.role.member')}</option>
              <option value="guest">{t('invite.role.guest')}</option>
            </select>
          </label>
          <div className="dialog__actions">
            <button type="button" onClick={onClose}>
              {t('invite.close')}
            </button>
            <button type="submit">{t('invite.send')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function SearchDialog({
  workspaceId,
  onClose,
  onOpen,
}: {
  workspaceId: string;
  onClose: () => void;
  onOpen: (id: string) => void;
}): React.ReactElement {
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<PageSummary[]>([]);
  const [active, setActive] = useState(0);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (query.trim().length === 0) {
      setHits([]);
      return;
    }
    const timer = window.setTimeout(() => {
      void searchPages(workspaceId, query.trim()).then((found) => {
        setHits(found);
        setActive(0);
      });
    }, 200);
    return () => window.clearTimeout(timer);
  }, [query, workspaceId]);

  return (
    <div
      className="overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="palette" role="dialog" aria-modal="true" aria-label={t('search.title')}>
        <input
          ref={inputRef}
          className="palette__input"
          aria-label={t('search.placeholder')}
          placeholder={t('search.placeholder')}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') onClose();
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setActive((value) => Math.min(value + 1, Math.max(hits.length - 1, 0)));
            }
            if (event.key === 'ArrowUp') {
              event.preventDefault();
              setActive((value) => Math.max(value - 1, 0));
            }
            if (event.key === 'Enter' && hits[active]) onOpen(hits[active].id);
          }}
        />
        <div className="palette__list">
          {query.trim() && hits.length === 0 ? (
            <div className="palette__empty">{t('search.empty')}</div>
          ) : null}
          {hits.map((hit, index) => (
            <button
              key={hit.id}
              type="button"
              className={index === active ? 'palette__item is-active' : 'palette__item'}
              onClick={() => onOpen(hit.id)}
            >
              <span style={{ flexGrow: 1, minWidth: 0 }}>
                <span className="palette__title">{hit.title || t('page.untitled')}</span>
                <span className="palette__sub">{t('search.openHint')}</span>
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function TrashDialog({
  workspaceId,
  onClose,
  onChanged,
}: {
  workspaceId: string;
  onClose: () => void;
  onChanged: () => void;
}): React.ReactElement {
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [items, setItems] = useState<PageSummary[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    void fetchTrash(workspaceId)
      .then((listed) => {
        setItems(listed);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, [workspaceId]);

  const shown = query.trim()
    ? items.filter((p) =>
        (p.title || t('page.untitled')).toLowerCase().includes(query.trim().toLowerCase()),
      )
    : items;

  return (
    <div
      className="overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="palette palette--sm"
        role="dialog"
        aria-modal="true"
        aria-label={t('sidebar.trash')}
      >
        <input
          ref={inputRef}
          className="palette__input"
          placeholder={t('trash.searchPlaceholder')}
          aria-label={t('trash.searchPlaceholder')}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') onClose();
          }}
        />
        <div className="palette__list">
          {shown.map((item) => (
            <div key={item.id} className="palette__item" style={{ cursor: 'default' }}>
              <span style={{ flexGrow: 1, minWidth: 0 }}>
                <span className="palette__title">{item.title || t('page.untitled')}</span>
                <span className="palette__sub">{t('trash.sub')}</span>
              </span>
              <span className="palette__actions">
                <button
                  type="button"
                  className="is-restore"
                  onClick={() => {
                    void restorePage(item.id).then(() => {
                      onChanged();
                      setItems((list) => list.filter((p) => p.id !== item.id));
                    });
                  }}
                >
                  {t('page.restore')}
                </button>
                <button
                  type="button"
                  className="is-delete"
                  onClick={() => {
                    void deletePage(item.id).then(() => {
                      onChanged();
                      setItems((list) => list.filter((p) => p.id !== item.id));
                    });
                  }}
                >
                  {t('trash.delete')}
                </button>
              </span>
            </div>
          ))}
          {loaded && items.length === 0 ? (
            <div className="palette__empty">{t('trash.empty')}</div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
